import { EmployeeWelcome } from './employee/employee-welcome';
import { usesEmployeeEntry } from './employee/employee-entry';
import { CommonModule } from '@angular/common';
import { Component, DOCUMENT, DestroyRef, EnvironmentInjector, OnInit, computed, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { serverTimestamp } from '@angular/fire/firestore';
import { NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router } from '@angular/router';
import { NativeMarket } from '@capacitor-community/native-market';
import { App, AppInfo } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { Device } from '@capacitor/device';
import { Dialog } from '@capacitor/dialog';
import { SplashScreen } from '@capacitor/splash-screen';
import { StatusBar, Style } from '@capacitor/status-bar';
import { ModalController, Platform } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { AppUpdateService } from '@scandium-oy/ngx-scandium';
import { zip } from 'rxjs';
import { debounceTime, filter, map, switchMap, take } from 'rxjs/operators';
import { register } from 'swiper/element/bundle';
import { AppCommonModule } from './common.module';
import { BootstrapLoadingOverlayComponent } from './components/bootstrap-loading-overlay/bootstrap-loading-overlay.component';
import { AppShellComponent } from './components2/shell/shell.component';
import { SwipeDirective } from './directives/swipe/swipe.directive';
import { User } from './models/user.model';
import { NavDialogComponent } from './nav/nav.dialog';
import { FirstLoginDialogComponent } from './on-boarding/first-login/first-login.dialog';
import { PendingSiteActivationDialogComponent } from './on-boarding/pending-site-activation.dialog';
import { InitializeService } from './services/initialize.service';
import { NavigationService } from './services/navigation.service';
import { StripeService } from './services/stripe.service';
import { ThemeService } from './services/theme.service';
import { UsersService } from './services/users.service';
import { VersionService } from './services/version.service';
import { AppReferenceDataStore } from './state/app-reference-data.store';
import { LANGUAGES } from './utility/constants';
import { getDuration, setStartTime } from './utility/time';
import { hasApplicationAccess } from './utility/site-activation';

register();

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    AppCommonModule,
    CommonModule,
    SwipeDirective,
    AppShellComponent,
    BootstrapLoadingOverlayComponent,
  ],
})
export class AppComponent implements OnInit {
  private documentRef = inject(DOCUMENT);
  private themeService = inject(ThemeService);
  private referenceDataStore = inject(AppReferenceDataStore);

  private destroyRef = inject(DestroyRef);
  private lastBack: number;
  private termsOpen = false;
  private pendingActivationOpening = false;
  private pendingActivationModal: HTMLIonModalElement | null = null;

  currentAccent = computed(() => {
    const accent = this.themeService.getAccentConfig()();

    this.documentRef.querySelector('meta[name=\'theme-color\']')?.setAttribute('content', accent.color);
    this.documentRef.body.style.setProperty('--accent-500', accent.color);
    this.documentRef.body.style.setProperty('--accent-600', accent.shade);
    this.documentRef.body.style.setProperty('--accent-100', accent.soft);
    this.documentRef.body.style.setProperty('--accent-050', accent.surface);
    this.documentRef.body.style.setProperty('--accent-contrast', accent.contrast);
    this.documentRef.body.style.setProperty('--accent-contrast-rgb', accent.contrastRgb);

    this.documentRef.body.style.setProperty('--ion-color-primary', accent.color);
    this.documentRef.body.style.setProperty('--ion-color-primary-rgb', accent.rgb);
    this.documentRef.body.style.setProperty('--ion-color-primary-contrast', accent.contrast);
    this.documentRef.body.style.setProperty('--ion-color-primary-contrast-rgb', accent.contrastRgb);
    this.documentRef.body.style.setProperty('--ion-color-primary-shade', accent.shade);
    this.documentRef.body.style.setProperty('--ion-color-primary-tint', accent.tint);

    return accent.value;
  });

  role = this.usersService.role;

  loading$ = this.router.events.pipe(
    filter(
      (e) =>
        e instanceof NavigationStart ||
        e instanceof NavigationEnd ||
        e instanceof NavigationCancel ||
        e instanceof NavigationError,
    ),
    map((e) => e instanceof NavigationStart),
  );

  loadingSites = computed(() => !this.referenceDataStore.sitesReady());
  isLogged = computed(() => this.usersService.currentUserS() != null);
  showTabs = signal(false);

  constructor(
    private appUpdateService: AppUpdateService,
    private initializeService: InitializeService,
    private injectorContext: EnvironmentInjector,
    private modalCtrl: ModalController,
    private navigationService: NavigationService,
    private platform: Platform,
    private router: Router,
    private stripeService: StripeService,
    private translate: TranslateService,
    private usersService: UsersService,
    private versionService: VersionService,
  ) {
    this.platform.backButton.subscribeWithPriority(-1, () => {
      if (Date.now() - this.lastBack < 500) {
        App.exitApp();
      } else {
        this.lastBack = Date.now();
        this.navigationService.back();
      }
    });
  }

  onSwipeBack() {
    if (this.platform.is('ios') && this.platform.is('capacitor')) {
      this.navigationService.back();
    }
  }

  ngOnInit(): void {
    this.initializeApp();
  }

  private openTerms(user: User) {
    if (!this.termsOpen) {
      console.info('Showing terms');
      this.termsOpen = true;
      this.modalCtrl.create({
        component: usesEmployeeEntry(user?.role) ? EmployeeWelcome : FirstLoginDialogComponent,
        ...usesEmployeeEntry(user?.role) ? {canDismiss: async (data: {workerAccepted?: boolean}) => data?.workerAccepted === true} : {},
        backdropDismiss: false,
        cssClass: ['workspace-modal', 'stack-modal'],
      },
      ).then((m) => {
        m.present();

        m.onDidDismiss().then(({ data }) => {
          if (data?.workerAccepted === true && usesEmployeeEntry(user?.role)) {
            this.termsOpen = false;
            void this.navigationService.navigateToHome();
          } else if (data?.acceptedTerms === true) {
            this.usersService.updateOnly(user.guid, { acceptedTerms: serverTimestamp() });
          } else {
            this.termsOpen = false;
          }
        });
      });
    }
  }

  private async syncPendingActivation(user: User | null) {
    const isPending = user?.accountAccessStatus === 'pending-site-activation';
    if (!isPending) {
      if (this.pendingActivationModal) {
        await this.pendingActivationModal.dismiss(null, 'activated');
        this.pendingActivationModal = null;
      }
      return;
    }
    if (this.pendingActivationModal || this.pendingActivationOpening) {
      return;
    }

    this.pendingActivationOpening = true;
    try {
      const modal = await this.modalCtrl.create({
        component: PendingSiteActivationDialogComponent,
        backdropDismiss: false,
        canDismiss: async (_data?: unknown, role?: string) => role === 'logout' || role === 'activated',
        cssClass: ['workspace-modal', 'stack-modal'],
      });
      this.pendingActivationModal = modal;
      await modal.present();
      void modal.onDidDismiss().then(() => {
        if (this.pendingActivationModal === modal) {
          this.pendingActivationModal = null;
        }
      });
    } finally {
      this.pendingActivationOpening = false;
    }
  }

  private async androidOverlayFixes() {
    const info = await Device.getInfo();
    const osVersion = info.osVersion;
    const insetDefault = parseInt(osVersion) >= 15 ? 20 : 0;
    if (parseInt(osVersion) <= 14) {
      StatusBar.setOverlaysWebView({ overlay: false });
    }
    this.computeSafeAreaInsets(insetDefault);
  }

  private computeSafeAreaInsets(insetDefault = 0) {
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.clientHeight;
    const bottomInset = windowHeight - documentHeight;
    document.documentElement.style.setProperty(
      '--my-android-inset',
      `${Math.max(bottomInset, insetDefault)}px`,
    );
  }

  private initializeApp() {
    console.info('Initialize App');
    this.setupLang();
    this.stripeService.initialize();
    if (Capacitor.isNativePlatform()) {
      SplashScreen.hide().catch((err) => {
        console.warn(err);
      });
      StatusBar.setStyle({
        style: Style.Light,
      }).catch((err) => {
        console.warn(err);
      });
      App.getInfo().then((info) => this.checkLatestVersion(info));
    }
    // Subscribe to streams
    // App updates
    this.appUpdateService.getVersionUpdates().pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe();

    // User
    this.initializeService.loadUser().pipe(
      debounceTime(50),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((loggedIn) => {
      const user = this.usersService.currentUserS();
      void this.syncPendingActivation(user);
      if (!loggedIn) {
        const url = this.router.url.split('?')[0];
        if (!['/sign-up', '/privacy', '/login/sign-in'].includes(url) && !url.startsWith('/instructions/public') && !url.startsWith('/signature/')) {
          this.navigationService.navigateToLogin(url);
        }
      } else if (hasApplicationAccess(user)) {
        if (!user?.acceptedTerms) {
          this.openTerms(user);
        }
      }
    });

    setStartTime();
    zip([
      this.initializeService.loadUsers(),
      this.initializeService.loadContractor(),
      this.initializeService.loadClientsData(),
      this.initializeService.loadSitesData(),
    ]).pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => console.info(getDuration(), 'Data loaded'));

    toObservable(this.loadingSites, { injector: this.injectorContext }).pipe(
      filter((loading) => !loading),
      switchMap(() => zip([
        this.initializeService.loadGlobalOptionsData(),
        this.initializeService.loadMaterialsData(),
      ])),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => console.info(getDuration(), 'Options and materials loaded'));
  }

  private setupLang() {
    // this language will be used as a fallback when a translation isn't found in the current language
    this.translate.setFallbackLang(LANGUAGES[0]);
    this.translate.addLangs(LANGUAGES);
    this.translate.use(LANGUAGES[0]);
  }

  private checkLatestVersion(info: AppInfo) {
    const build = +info.build;
    const guid = Capacitor.getPlatform();
    this.versionService.get(guid).pipe(
      take(1),
    ).subscribe((version) => {
      const latestBuild = +(version?.build ?? 0);
      if (latestBuild > build) {
        Dialog.confirm({
          title: this.translate.instant('version.title'),
          message: this.translate.instant('version.message'),
          okButtonTitle: this.translate.instant('version.ok'),
          cancelButtonTitle: this.translate.instant('version.cancel'),
        }).then((result) => {
          if (result.value) {
            // Open store
            NativeMarket.openStoreListing({
              appId: 'fi.scandium.urakka',
            });
          } else {
            App.exitApp();
          }
        });
      }
    });
  }

  onHome() {
    setTimeout(() => {
      this.navigationService.navigateToHome();
    }, 50);
  }

  onMenu() {
    this.modalCtrl.create({ component: NavDialogComponent, cssClass: ['workspace-modal'] }).then(async (m) => m.present());
  }
}
